import React, { useEffect, useMemo, useState } from 'react';

import {
  LTButton,
  LTCopyToClipboard,
  LTText,
  LTUnderlineNav,
} from '@lambdatestincprivate/lt-components';
import { themeGet } from '@primer/react';
import get from 'lodash/get';
import styled from 'styled-components';

import HistoryList from './HistoryList';
import RetryList from './RetryList';
import { useTestHistory } from './useTestHistory';
import { useTestRetryHistory } from './useTestRetryHistory';
import { HistoryTypeMap } from '../../constants/test';
import { getEnvVar } from '../../utils/env';
import { getCookieData } from '../../utils/cookieHandler/handler';
import { getUrlByUserDomain } from '../../utils/utilityFunctions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 97px);
  min-height: 0;
  .navWrapper {
    margin: 0px -16px;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const IdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  margin: 0 -16px;
  padding: 16px 16px 16px 16px;
  height: 64px;
  flex-shrink: 0;
  border-top: 1px solid;
  border-color: ${themeGet('colors.border.default')};
`;

function resolveApiBaseUrl() {
  const fullUrl = getEnvVar('REACT_APP_BASE_API_URL');
  if (fullUrl) {
    return fullUrl;
  }

  const prefix = getEnvVar('REACT_APP_API_BASE_URL_PREFIX');
  if (prefix) {
    return getUrlByUserDomain(prefix);
  }
  return getEnvVar('REACT_APP_INSIGHTS_API_URL') || '';
}

export function resolveAnalyticsUrl() {
  const prefix = getEnvVar('REACT_APP_ML_ANALYTICS_PREFIX');
  if (prefix) {
    return getUrlByUserDomain(prefix);
  }
  return getEnvVar('REACT_APP_MAGICLEAP_URL') || '';
}

function resolveAuthToken() {
  const cookieName =
    getEnvVar('REACT_APP_COOKIE_NAME') ||
    getEnvVar('REACT_APP_JWT_COOKIE_NAME') ||
    '';
  return getCookieData(cookieName) || '';
}

/**
 * Self-contained Test History component with optional Reproduce/Retry tabs.
 * No Redux dependency.
 *
 * @param {Object}  props
 * @param {Object}  props.testDetail     - Test detail (needs id, name, type, mode, feature_flag, reproduce_count, replay_count)
 * @param {Object}  [props.userInfo]     - User info for retry API auth (needs data.username, data.apiToken)
 * @param {string}  [props.apiBaseUrl]   - Override for API base URL
 * @param {string}  [props.authToken]    - Override for auth token
 * @param {string}  [props.analyticsUrl] - Override for analytics URL
 * @param {string}  [props.index]        - Override for Elasticsearch index
 */
const TestHistory = ({
  testDetail,
  userInfo,
  apiBaseUrl: apiBaseUrlProp,
  authToken: authTokenProp,
  analyticsUrl: analyticsUrlProp,
  index,
}) => {
  const apiBaseUrl = useMemo(() => apiBaseUrlProp ?? resolveApiBaseUrl(), [apiBaseUrlProp]);
  const authToken = useMemo(() => authTokenProp ?? resolveAuthToken(), [authTokenProp]);
  const analyticsUrl = useMemo(() => analyticsUrlProp ?? resolveAnalyticsUrl(), [analyticsUrlProp]);

  const [selectedNav, setSelectedNav] = useState(HistoryTypeMap.HISTORY);

  const { id = '', name: testName = '', reproduce_count = 0, replay_count = 0 } = testDetail || {};

  const showTabs = userInfo && (Number(reproduce_count) > 0 || Number(replay_count) > 0);

  // History tab data
  const {
    data: historyData,
    isLoading: historyLoading,
    isSilentLoading,
    hasMore,
    fetchHistory,
    loadMore,
  } = useTestHistory({ testDetail, apiBaseUrl, authToken, index });

  // Retry/Reproduce tab data
  const retryUsername = get(userInfo, ['data', 'username'], '');
  const retryApiToken = get(userInfo, ['data', 'apiToken'], '');

  const {
    data: reproduceData,
    isLoading: reproduceLoading,
    fetchHistory: fetchReproduceHistory,
  } = useTestRetryHistory({ apiBaseUrl, username: retryUsername, apiToken: retryApiToken });

  const {
    data: retryData,
    isLoading: retryLoading,
    fetchHistory: fetchRetryHistory,
  } = useTestRetryHistory({ apiBaseUrl, username: retryUsername, apiToken: retryApiToken });

  // Fetch history on mount / test change
  useEffect(() => {
    if (testDetail && apiBaseUrl && authToken) {
      fetchHistory(0, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-fetch when test ID changes
  }, [testDetail?.id]);

  const getNavs = () => {
    const navs = [
      {
        contentId: 'test-history-history',
        id: 'test-history-history-tab',
        label: 'History',
        key: HistoryTypeMap.HISTORY,
        isSelected: selectedNav === HistoryTypeMap.HISTORY,
      },
    ];

    if (Number(reproduce_count) > 0) {
      navs.push({
        contentId: 'test-history-reproduce',
        id: 'test-history-reproduce-tab',
        label: 'Reproduced',
        key: HistoryTypeMap.REPRODUCE,
        isSelected: selectedNav === HistoryTypeMap.REPRODUCE,
      });
    }

    if (Number(replay_count) > 0) {
      navs.push({
        contentId: 'test-history-replay',
        id: 'test-history-replay-tab',
        label: 'Retries',
        key: HistoryTypeMap.REPLAY,
        isSelected: selectedNav === HistoryTypeMap.REPLAY,
      });
    }

    return navs;
  };

  const handleNavChange = (nav) => {
    const tabKey = nav?.key;
    if (tabKey === selectedNav) return;

    if (tabKey === HistoryTypeMap.REPRODUCE && (!reproduceData?.tests || reproduceData.tests.length === 0)) {
      fetchReproduceHistory(id, HistoryTypeMap.REPRODUCE);
    } else if (tabKey === HistoryTypeMap.REPLAY && (!retryData?.tests || retryData.tests.length === 0)) {
      fetchRetryHistory(id, HistoryTypeMap.REPLAY);
    }

    setSelectedNav(tabKey);
  };

  const showFooter =
    selectedNav === HistoryTypeMap.HISTORY &&
    !historyLoading &&
    historyData.length > 0 &&
    analyticsUrl;

  return (
    <Container>
      <ItemContainer>
        <IdRow>
          <LTText
            variant={LTText.TextVariant.SUBHEADER_REGULAR}
            color="fg.muted"
          >
            {`Test ID: ${id}`}
          </LTText>
          <LTCopyToClipboard textToCopy={id} />
        </IdRow>
        <LTText
          variant={LTText.TextVariant.SUBHEADER_BOLD}
          style={{ wordBreak: 'break-word' }}
        >
          {testName || id}
        </LTText>
      </ItemContainer>

      {showTabs && (
        <div className="navWrapper">
          <LTUnderlineNav
            ariaLabel="Test History Tabs"
            navs={getNavs()}
            onClick={handleNavChange}
            styles={{ padding: '0px 16px' }}
            tabPanelId="test-history-nav"
          />
        </div>
      )}

      <div
        aria-labelledby={showTabs ? `test-history-${selectedNav}-tab` : undefined}
        id={showTabs ? `test-history-${selectedNav}` : undefined}
        role={showTabs ? 'tabpanel' : undefined}
        style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {selectedNav === HistoryTypeMap.HISTORY && (
          <HistoryList
            data={historyData}
            isLoading={historyLoading}
            isSilentLoading={isSilentLoading}
            hasMore={hasMore}
            parentTestId={id}
            onLoadMore={loadMore}
          />
        )}
        {selectedNav === HistoryTypeMap.REPRODUCE && (
          <RetryList data={reproduceData} isLoading={reproduceLoading} />
        )}
        {selectedNav === HistoryTypeMap.REPLAY && (
          <RetryList data={retryData} isLoading={retryLoading} />
        )}
      </div>

      {showFooter && (
        <FooterContainer>
          <LTButton
            onClick={() => {
              const url = `${analyticsUrl.replace(/\/+$/, '')}/test-insights/${btoa(testName || id)}`;
              window.open(url);
            }}
            size={LTButton.Size.MEDIUM}
          >
            View more insights
          </LTButton>
        </FooterContainer>
      )}
    </Container>
  );
};

export default TestHistory;
